/**
 * Skinning pe placa video: fiecare vârf al modelului e legat de cele mai apropiate 4 noduri ale caroseriei,
 * cu ponderi 1/d². Pozițiile nodurilor stau într-o textură (RGBA float, 64 pe rând); shaderul mută vârful cu
 * media ponderată a deplasărilor nodurilor față de repaus: v' = v + Σ wᵢ·(Pᵢ − Pᵢ⁰).
 */
import * as THREE from 'three/webgpu';
import { attribute, Fn, int, ivec2, positionLocal, textureLoad } from 'three/tsl';

export const TEX_W = 64;

export interface Binding { index: Float32Array; weight: Float32Array }

/** Cele mai apropiate k noduri (dintre `candidates`) pentru fiecare vârf, cu ponderi normalizate 1/(d² + ε). */
export function bindVertices(verts: ArrayLike<number>, nodes: ArrayLike<number>, candidates: ArrayLike<number>, k = 4): Binding {
  const nv = verts.length / 3;
  const index = new Float32Array(nv * 4), weight = new Float32Array(nv * 4);
  const bestD = new Float64Array(k), bestI = new Int32Array(k);
  for (let v = 0; v < nv; v++) {
    const x = verts[v * 3], y = verts[v * 3 + 1], z = verts[v * 3 + 2];
    bestD.fill(Infinity); bestI.fill(-1);
    for (let c = 0; c < candidates.length; c++) {
      const n = candidates[c];
      const dx = nodes[n * 3] - x, dy = nodes[n * 3 + 1] - y, dz = nodes[n * 3 + 2] - z;
      const d = dx * dx + dy * dy + dz * dz;
      if (d >= bestD[k - 1]) continue;
      let j = k - 1;
      while (j > 0 && bestD[j - 1] > d) { bestD[j] = bestD[j - 1]; bestI[j] = bestI[j - 1]; j--; }
      bestD[j] = d; bestI[j] = n;
    }
    let sum = 0;
    for (let j = 0; j < k; j++) if (bestI[j] >= 0) sum += 1 / (bestD[j] + 1e-3);
    for (let j = 0; j < 4; j++) {
      const ok = j < k && bestI[j] >= 0;
      index[v * 4 + j] = ok ? bestI[j] : 0;
      weight[v * 4 + j] = ok ? 1 / (bestD[j] + 1e-3) / sum : 0;
    }
  }
  return { index, weight };
}

export function nodeTexture(count: number, data?: ArrayLike<number>): THREE.DataTexture {
  const h = Math.max(1, Math.ceil(count / TEX_W));
  const arr = new Float32Array(TEX_W * h * 4);
  if (data) writeNodes(arr, data, count);
  const t = new THREE.DataTexture(arr, TEX_W, h, THREE.RGBAFormat, THREE.FloatType);
  t.needsUpdate = true;
  return t;
}

export function writeNodes(target: Float32Array, xyz: ArrayLike<number>, count: number): void {
  for (let i = 0; i < count; i++) {
    target[i * 4] = xyz[i * 3];
    target[i * 4 + 1] = xyz[i * 3 + 1];
    target[i * 4 + 2] = xyz[i * 3 + 2];
    target[i * 4 + 3] = 1;
  }
}

/** Nodul de poziție al materialului: vârful plus deplasarea ponderată a celor 4 noduri. */
export function skinnedPosition(cur: THREE.DataTexture, rest: THREE.DataTexture) {
  return Fn(() => {
    const idx = attribute('nbIndex', 'vec4');
    const w = attribute('nbWeight', 'vec4');
    type F = typeof idx.x;
    const at = (t: THREE.DataTexture, f: F) => {
      const i = int(f);
      return textureLoad(t, ivec2(i.mod(int(TEX_W)), i.div(int(TEX_W)))).xyz;
    };
    const d = (f: F, wk: F) => at(cur, f).sub(at(rest, f)).mul(wk);
    return positionLocal.add(d(idx.x, w.x)).add(d(idx.y, w.y)).add(d(idx.z, w.z)).add(d(idx.w, w.w));
  })();
}

/** Geometrie cu pozițiile și normalele în float, transformată în spațiul mașinii (repaus). */
export function bakeGeometry(mesh: THREE.Mesh): THREE.BufferGeometry {
  const src = mesh.geometry;
  const g = new THREE.BufferGeometry();
  for (const name of ['position', 'normal'] as const) {
    const a = src.getAttribute(name);
    if (!a) continue;
    const out = new Float32Array(a.count * 3);
    for (let i = 0; i < a.count; i++) { out[i * 3] = a.getX(i); out[i * 3 + 1] = a.getY(i); out[i * 3 + 2] = a.getZ(i); }
    g.setAttribute(name, new THREE.BufferAttribute(out, 3));
  }
  if (src.index) g.setIndex(Array.from(src.index.array as ArrayLike<number>));
  mesh.updateWorldMatrix(true, false);
  g.applyMatrix4(mesh.matrixWorld);
  if (!g.getAttribute('normal')) g.computeVertexNormals();
  return g;
}
